import React, { useEffect, useState } from 'react'
import { Button, Grid, makeStyles, Typography } from '@material-ui/core'
import { Box } from '@mui/material'
import { singleSubjectInviteSchema } from 'schemas/subjects'
import { useDispatch, useSelector } from 'react-redux'
import CustomTextField from 'components/Form/CustomTextField'
import { Form, Formik } from 'formik'
import { createSubject, sendInviteEmailToSpecificSubject, submitSubjectData } from 'store/subject'
import ProgressButton from 'components/ProgressButton'
import { useHistory, useLocation } from 'react-router-dom'
import CustomBox from 'components/Form/CustomBox'
import { Roles, speakerStatus, SubjectStatus, SubjectType } from 'constants/index'
import { fetchConferenceById } from 'store/conference'
import { nanoid } from 'nanoid'
import { toast } from 'react-toastify'
import { addSpeaker, getExistingSpeakerWithEmail, linkSubjectToSpeaker } from 'store/speaker'
import { placeSpecificImageToSubjectPath } from 'utils/subject'
import { getExistingExhibitorWithEmail, linkSubjectToExhibitor } from 'store/vendor'
import { getExistingSponsorWithEmail, linkSubjectToSponsor } from 'store/sponsor'

const SingleSubjectInviteForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const subjectType = new URLSearchParams(location.search).get('type')
  const conferenceId = new URLSearchParams(location.search).get('conferenceId')
  const { user } = useSelector((state) => state.auth)
  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    if (user.role === Roles.SUPER_ADMIN && conferenceId) {
      dispatch(fetchConferenceById(conferenceId))
    } else {
      dispatch(fetchConferenceById(user.conferenceId))
    }
  }, [dispatch])

  const goBack = () => {
    history.goBack()
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    const confId = user?.role === Roles.SUPER_ADMIN ? conferenceId : user?.conferenceId
    const payload = {
      email: values.email,
      type: subjectType,
      token: nanoid(),
      status: SubjectStatus.INVITE_NOT_SENT,
      conferenceId: confId
    }
    if (subjectType === SubjectType.EXHIBITOR) {
      payload.name = values.fullName
    } else {
      payload.fullName = values.fullName
    }

    let existingSpeaker = null
    let existingExhibitor = null
    let existingSponsor = null
    if (payload.type === SubjectType.SPEAKER) {
      existingSpeaker = await getExistingSpeakerWithEmail(values?.email.trim(), confId)
      if (existingSpeaker?.id) {
        payload.fullName = existingSpeaker?.fullName
        payload.bio = existingSpeaker?.bio
        payload.company = existingSpeaker?.company
        payload.position = existingSpeaker?.position
        payload.websiteUrl = existingSpeaker?.websiteUrl
        payload.speakerId = existingSpeaker?.id
      }

      if (existingSpeaker?.imageName) {
        payload.imageName = existingSpeaker?.imageName
        await placeSpecificImageToSubjectPath(
          existingSpeaker?.imageName,
          'speakers',
          'subjects',
          confId
        )
      }
    } else if (payload.type === SubjectType.EXHIBITOR) {
      existingExhibitor = await getExistingExhibitorWithEmail(values?.email.trim(), confId)
      if (existingExhibitor?.id) {
        payload.email = existingExhibitor?.email
        payload.name = existingExhibitor?.name
        payload.booth = existingExhibitor?.booth
        payload.intro = existingExhibitor?.intro
        payload.message = existingExhibitor?.message
        payload.websiteUrl = existingExhibitor?.websiteUrl
        payload.vendorId = existingExhibitor?.id
      }

      if (existingExhibitor?.logoImage) {
        payload.logoImage = existingExhibitor?.logoImage
        await placeSpecificImageToSubjectPath(
          existingExhibitor?.logoImage,
          'vendors',
          'subjects',
          confId
        )
      }
    } else if (payload.type === SubjectType.SPONSOR) {
      existingSponsor = await getExistingSponsorWithEmail(values?.email.trim(), confId)
      if (existingSponsor?.id) {
        payload.email = existingSponsor?.email
        payload.fullName = existingSponsor?.fullName
        payload.intro = existingSponsor?.intro
        payload.sponsorAdTargetURL = existingSponsor?.sponsorAdTargetURL
        payload.sponsorMessage = existingSponsor?.sponsorMessage
        payload.sponsorshipLevelId = existingSponsor?.sponsorshipLevelId
        payload.websiteUrl = existingSponsor?.websiteUrl
        payload.speakerId = existingSponsor?.id
      }

      const promises = []
      if (existingSponsor?.logoImageName) {
        payload.logoImageName = existingSponsor?.logoImageName
        promises.push(
          placeSpecificImageToSubjectPath(
            existingSponsor?.logoImageName,
            'sponsors',
            'subjects',
            confId
          )
        )
      }
      if (existingSponsor?.adImageName) {
        payload.adImageName = existingSponsor?.adImageName
        promises.push(
          placeSpecificImageToSubjectPath(
            existingSponsor?.adImageName,
            'sponsors',
            'subjects',
            confId
          )
        )
      }
      if (existingSponsor?.brochureImageName) {
        payload.brochureImageName = existingSponsor?.brochureImageName
        promises.push(
          placeSpecificImageToSubjectPath(
            existingSponsor?.brochureImageName,
            'sponsors',
            'subjects',
            confId
          )
        )
      }
      await Promise.all(promises)
    }

    const subjectId = await dispatch(createSubject(payload))
    if (subjectId) {
      if (!existingSpeaker?.id && payload.type === SubjectType.SPEAKER) {
        const speakerPayload = { ...payload }
        speakerPayload?.type && delete speakerPayload['type']
        speakerPayload?.token && delete speakerPayload['token']
        speakerPayload.isSubject = true
        speakerPayload.status = speakerStatus.PENDING
        speakerPayload.subjectId = subjectId
        const speakerId = await dispatch(addSpeaker(speakerPayload, confId))
        await dispatch(submitSubjectData(subjectId, { speakerId }))
      }

      existingSpeaker?.id && (await linkSubjectToSpeaker(existingSpeaker?.id, subjectId))
      existingExhibitor?.id && (await linkSubjectToExhibitor(existingExhibitor?.id, subjectId))
      existingSponsor?.id && (await linkSubjectToSponsor(existingSponsor?.id, subjectId))
      toast.success(`Subject as ${subjectType} created successfully.`)
      await sendInviteEmailToSpecificSubject(subjectId, confId)
      goBack()
    }
    setLoading(false)
  }

  const initialValues = {
    fullName: '',
    email: ''
  }
  return (
    <CustomBox>
      <Box className={classes.headBar}>
        <Typography variant="h4">Create & Invite {subjectType}</Typography>
        <Button color="primary" variant="contained" onClick={goBack}>
          Back
        </Button>
      </Box>

      <Box p={1} border={1} sx={{ mb: '2rem' }}>
        <Box
          p={1}
          sx={{
            backgroundColor: '#ECECEC',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem'
          }}>
          <Box className={classes.singleInfo}>
            <Typography className={classes.label}>Conference Name:</Typography>
            <Typography>{conference?.confName}</Typography>
          </Box>
        </Box>
      </Box>

      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={() => singleSubjectInviteSchema(subjectType)}
        onSubmit={handleSubmit}>
        {() => (
          <Form autoComplete="off" style={{ marginBottom: '1rem', marginTop: '1rem' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  name="fullName"
                  type="text"
                  label={subjectType === SubjectType.SPONSOR ? 'Company' : subjectType + ' Name'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField name="email" type="email" label={`${subjectType} Email`} />
              </Grid>

              <Grid item xs={12} md={12}>
                <Box className={classes.buttonBox}>
                  <Button size="large" disabled={loading} onClick={goBack}>
                    Cancel
                  </Button>
                  <ProgressButton
                    type="submit"
                    inProgress={loading}
                    disabled={loading}
                    label="Create & Invite"
                  />
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </CustomBox>
  )
}

export default SingleSubjectInviteForm

const useStyles = makeStyles({
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0rem 0rem 2rem 0rem'
  },
  singleInfo: {
    display: 'flex',
    gap: '2rem'
  },
  label: {
    minWidth: '132px',
    fontSize: '15px',
    fontWeight: '600'
  },
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '0.75rem'
  }
})
